import { useSelector } from 'react-redux';
import {
  SELECTOR_PLAYBACK_CURRENT_TIME,
  SELECTOR_PLAYBACK_DURATION,
  SELECTOR_PLAYBACK_VOLUME_PERCENT,
  SELECTOR_PLAYBACK_BUFFERED,
  SELECTOR_PLAYBACK_HAS_NATIVE_HLS,
} from 'src/store/reducers/playback';
import {
  SELECTOR_RESOLUTION,
  SELECTOR_RESOLUTION_VIDEO_PLAYBACK_QUALITY,
  SELECTOR_RESOLUTION_MEDIA_INFO,
} from 'src/store/reducers/resolution';
import { formatBandwidth, formatSeconds } from 'src/utils/formatUtils';
import { detectConnectionType, getBrowserInfo } from 'src/utils/browserUtils';
import ApplicationService from 'src/services/ApplicationService';
import { IMediaStats, IGeneralStats, IBufferStats } from 'src/interfaces/Stats';
import { useContext } from 'react';
import { ScreenSizeContext } from 'src/contexts/ScreenSizeContext';
import projectPackage from '../../../package.json';
import useApplicationData from '../useApplicationData';

const useStats = () => {
  const duration = useSelector(SELECTOR_PLAYBACK_DURATION);
  const mediaCurrentTime = useSelector(SELECTOR_PLAYBACK_CURRENT_TIME);
  const volumePercent = useSelector(SELECTOR_PLAYBACK_VOLUME_PERCENT);
  const buffered = useSelector(SELECTOR_PLAYBACK_BUFFERED);
  const hasNativeHLS = useSelector(SELECTOR_PLAYBACK_HAS_NATIVE_HLS);

  const resolution = useSelector(SELECTOR_RESOLUTION);
  const videoPlaybackQuality = useSelector(SELECTOR_RESOLUTION_VIDEO_PLAYBACK_QUALITY);
  const mediaInfo = useSelector(SELECTOR_RESOLUTION_MEDIA_INFO);

  const { mediaCode, userCode, experimentName } = useApplicationData();
  const { width: windowWidth, height: windowHeight } = useContext(ScreenSizeContext);
  const { droppedVideoFrames, totalVideoFrames } = videoPlaybackQuality;

  function mediaResolution() {
    return `${mediaInfo.resolution.width}x${mediaInfo.resolution.height}`;
  }

  const mediaData: IMediaStats = {
    bandwidth: formatBandwidth(mediaInfo.bandwidth),
    codecs: mediaInfo.codecs,
    mediaCode: mediaCode,
    user: userCode ?? '',
    currentDate: `${new Date().toUTCString()}`,
    mediaResolution: mediaResolution(),
    deviceConnection: detectConnectionType(),
    chosenResolution: resolution.label,
    droppedVideoFrames: droppedVideoFrames,
    totalVideoFrames: totalVideoFrames,
  };

  const generalData: IGeneralStats = {
    hasNativeHLS,
    viewport: `${windowHeight}x${windowWidth}`,
    projectVersion: projectPackage.version,
    experimentName: experimentName || '',
    duration: formatSeconds(duration || 0),
    volume: ((volumePercent || 0) / 100).toFixed(2),
    browserInfo: getBrowserInfo(),
    viewId: ApplicationService.player?.viewId || '',
  };

  const bufferedData: IBufferStats = {
    currentTime: formatSeconds(mediaCurrentTime || 0),
    bufferEnd: buffered.end,
    bufferStart: buffered.start,
    bufferTimeRemaining: formatSeconds(buffered.end - mediaCurrentTime),
  };

  const stats = {
    media: mediaData,
    general: generalData,
    buffer: bufferedData,
  };

  return {
    stats,
  };
};

export default useStats;
